// Whether a shared video may carry the link to the page it came from.
//
// The server hands out a hike's or a recording's shareable URL whatever its privacy
// (`Hike#shareable_url`, `RecordingType#shareable_url`), so a private walk's video used to go out with
// a link nobody else could open. The share wizard therefore has to know the subject's privacy, and
// this module decides — per tile tap — whether the page link goes, stays off, or needs the owner's
// answer first:
//
//   public                     → the link goes, exactly as before.
//   private, viewer owns it    → ask once: make it public and share with the link / share the video
//                                only / cancel. The answer holds for the rest of that open wizard.
//   private, someone else's    → video only, silently: there is nothing they could do about it.
//   unknown / no page URL      → video only. No page link is better than a wrong one, and a share is
//                                never blocked on it.
//
// Only the tiles that put the page link into what they send ever ask (see targetCarriesPageLink); a
// tile that never carries it shares exactly as it always did.
//
// The `page_link` and `privacy_choice` wire values are shared with the apps (Android
// `FlyoverShareAnalytics` / `ShareCardAnalytics`, iOS `FlyoverShareAnalyticsEvent` /
// `ShareCardAnalyticsEvent`) — one PostHog project, so the strings must not drift.

import type { ShareTargetKey } from './shareAnalytics';

export type PageLinkSubjectType = 'hike' | 'recording';

/** What a share config knows about the privacy of the page its link points at. */
export interface PageLinkPrivacy {
  /** True when the page opens for anyone; false when it is private. Absent/null → not known. */
  isPublic?: boolean | null;
  /** The viewer owns the subject, so they are the one who could make it public. */
  viewerOwns?: boolean | null;
  /** What to make public, and its id for UpdateHikeDetails / UpdateRecording. */
  subjectType?: PageLinkSubjectType | null;
  subjectId?: string | null;
}

/** `page_link` on "Flyover Share Target Tapped". */
export type PageLinkStatus = 'attached' | 'omitted_private' | 'unavailable';

/** `privacy_choice`: what the owner answered when asked. */
export type PrivacyChoice = 'made_public' | 'shared_without_link' | 'cancelled';

/**
 * Whether a tile's payload would include the page link (shareActions.ts) — the tiles that ask:
 * - WhatsApp and email write it into the message, and the native share sheet (behind Instagram,
 *   Stories, Messenger and More on the web) sends it as the payload's `url`: always.
 * - Facebook's sharer and Copy link take ONE url — the video's, falling back to the page only while
 *   there is no video URL: so only then.
 * - Save video downloads the file: never.
 * Same rule as the apps, so a tile asks on every platform or on none.
 */
const SINGLE_LINK_TARGETS: readonly ShareTargetKey[] = ['facebook', 'facebookReel', 'link'];

export function targetCarriesPageLink(target: ShareTargetKey, hasVideoUrl = true): boolean {
  if (target === 'saveVideo') return false;
  if (SINGLE_LINK_TARGETS.includes(target)) return !hasVideoUrl;
  return true;
}

/** A subject's identity as a stable key, for remembering what was made public on this page. */
export function subjectKey(privacy: PageLinkPrivacy | null | undefined): string | null {
  const type = privacy?.subjectType;
  const id = (privacy?.subjectId ?? '').toString().trim();
  return type && id ? `${type}:${id}` : null;
}

/**
 * The privacy to act on, given what has already been made public from this page. A walk published
 * from one wizard stays published when the next one opens (the hike page's trailer thumb and its
 * menu item both open the wizard, each with the privacy the page rendered with), so it must not be
 * asked about twice.
 */
export function effectivePrivacy(
  privacy: PageLinkPrivacy | null | undefined,
  madePublic: ReadonlySet<string>,
): PageLinkPrivacy {
  const base = { ...(privacy ?? {}) };
  const key = subjectKey(base);
  if (key && madePublic.has(key)) base.isPublic = true;
  return base;
}

/** True when the viewer can be asked: private, theirs, and the subject is identifiable. */
export function canAskToMakePublic(privacy: PageLinkPrivacy | null | undefined): boolean {
  return privacy?.isPublic === false && privacy?.viewerOwns === true && subjectKey(privacy) !== null;
}

export type TargetPageLink =
  /** Share now; `pageUrl` is what to send as the page link ('' for none). */
  | { kind: 'share'; pageUrl: string; status: PageLinkStatus }
  /** Ask the owner first, then resolve again with their answer. */
  | { kind: 'ask' };

/**
 * What a tile tap does about the page link. `choice` is the answer already given in this open wizard
 * (null before any); 'cancelled' is not an answer, so it asks again.
 *
 * Every tile resolves here, including the ones that never carry the page link, so the caller has one
 * code path and every "Flyover Share Target Tapped" reports `page_link`. For those tiles the status
 * describes the item's link (attached if public, omitted_private if private, unavailable if unknown)
 * — the same on every platform — and the `pageUrl` is never sent while there is a video URL.
 */
export function resolveTargetPageLink(
  target: ShareTargetKey,
  shareUrl: string | null | undefined,
  privacy: PageLinkPrivacy | null | undefined,
  choice: PrivacyChoice | null,
  hasVideoUrl = true,
): TargetPageLink {
  const url = (shareUrl ?? '').trim();
  if (!url) return { kind: 'share', pageUrl: '', status: 'unavailable' };
  if (privacy?.isPublic === true || (privacy?.isPublic === false && choice === 'made_public')) {
    return { kind: 'share', pageUrl: url, status: 'attached' };
  }
  if (privacy?.isPublic !== false) return { kind: 'share', pageUrl: '', status: 'unavailable' };
  if (choice !== 'shared_without_link' && targetCarriesPageLink(target, hasVideoUrl) && canAskToMakePublic(privacy)) {
    return { kind: 'ask' };
  }
  return { kind: 'share', pageUrl: '', status: 'omitted_private' };
}

/** Reads the `"true"` / `"false"` a server-rendered data-* attribute carries; anything else is unknown. */
export function booleanFromDataset(value: string | null | undefined): boolean | null {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

/**
 * The privacy a server-rendered share element declares (data-page-public, data-viewer-owns,
 * data-subject-id — see ShareLinkPrivacyHelper). Unknown when the markup doesn't say, which keeps the
 * page link off rather than guessing.
 */
export function privacyFromDataset(
  ds: Record<string, string | undefined> | null | undefined,
  subjectType: PageLinkSubjectType,
): PageLinkPrivacy {
  return {
    isPublic: booleanFromDataset(ds?.pagePublic),
    viewerOwns: booleanFromDataset(ds?.viewerOwns) === true,
    subjectType,
    subjectId: (ds?.subjectId ?? '').trim() || null,
  };
}

/** A hike's page opens for anyone when it is publicly visible or official (HikesController#show). */
export function hikePagePrivacy(
  hike: { id?: string | number | null; publiclyVisible?: boolean | null; madeOfficialAt?: string | null } | null | undefined,
  viewerOwns: boolean,
): PageLinkPrivacy {
  const visible = hike?.publiclyVisible;
  let isPublic: boolean | null = null;
  if (visible === true || !!hike?.madeOfficialAt) isPublic = true;
  else if (visible === false) isPublic = false;
  return {
    isPublic,
    viewerOwns,
    subjectType: 'hike',
    subjectId: hike?.id != null && String(hike.id).trim() !== '' ? String(hike.id) : null,
  };
}

/** A recording's public page opens for anyone only when the recording is public. */
export function recordingPagePrivacy(
  recording: { id?: string | number | null; public?: boolean | null } | null | undefined,
  viewerOwns: boolean,
): PageLinkPrivacy {
  const pub = recording?.public;
  return {
    isPublic: pub === true ? true : pub === false ? false : null,
    viewerOwns,
    subjectType: 'recording',
    subjectId: recording?.id != null && String(recording.id).trim() !== '' ? String(recording.id) : null,
  };
}
